import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const Footer = () => {
  const navigate = useNavigate();

  const footerLinks = {
    product: [
      { label: 'Features', path: '#features' },
      { label: 'Campaigns', path: '#campaigns' },
      { label: 'Analytics', path: '/analytics' },
      { label: 'Pricing', path: '#pricing' }
    ],
    company: [
      { label: 'About Us', path: '/about' },
      { label: 'Careers', path: '/careers' },
      { label: 'Blog', path: '/blog' },
      { label: 'Press Kit', path: '/press' }
    ],
    resources: [
      { label: 'Help Center', path: '/help-center' },
      { label: 'Creator Guide', path: '/creator-guide' },
      { label: 'Brand Guide', path: '/brand-guide' },
      { label: 'API Docs', path: '/api' }
    ],
    legal: [
      { label: 'Privacy Policy', path: '/privacy-policy' },
      { label: 'Terms of Service', path: '/terms-of-service' },
      { label: 'Cookie Policy', path: '/cookie-policy' },
      { label: 'GDPR', path: '/gdpr' }
    ],
    solutions: [
      { label: 'Influencer Marketing India', path: '/influencer-marketing-india' },
      { label: 'Find Influencers India', path: '/find-influencers-india' },
      { label: 'Micro Influencer Marketing', path: '/micro-influencer-marketing' },
      { label: 'Instagram Influencers', path: '/instagram-influencer-platform' },
      { label: 'YouTube Creators', path: '/youtube-creator-marketplace' },
      { label: 'UGC Creators', path: '/ugc-creator-platform' },
      { label: 'Brand Ambassadors', path: '/brand-ambassador-platform' },
      { label: 'NOONX vs Upfluence', path: '/noonx-vs-upfluence' }
    ]
  };

  const socialLinks = [
    { icon: 'Twitter', label: 'Twitter', url: '#' },
    { icon: 'Instagram', label: 'Instagram', url: '#' },
    { icon: 'Linkedin', label: 'LinkedIn', url: '#' },
    { icon: 'Youtube', label: 'YouTube', url: '#' },
    { icon: 'Github', label: 'GitHub', url: '#' }
  ];

  return (
    <footer className="bg-black text-white relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-red-400 to-primary"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        {/* Top section with logo and description */}
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 mb-24 pb-24 border-b border-white/10">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <Image
                src="/assets/images/Noonx_logo_small.png"
                alt="NOONX Platform Logo"
                className="h-12 w-auto"
              />
            </div>
            <p className="text-white/70 text-lg leading-relaxed mb-6 max-w-md">
              Connecting brands with creators through performance-based campaigns. Transform your influence into sustainable income.
            </p>
            
            {/* Newsletter signup */}
            <div className="flex flex-col sm:flex-row gap-6 max-w-md mt-6">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 bg-white/10 backdrop-blur-sm border border-primary/20 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:border-primary transition-all duration-300 text-lg"
              />
              <button className="px-10 py-4 bg-white text-primary rounded-2xl font-bold transition-all duration-300 hover:bg-primary hover:text-white hover:scale-105 text-lg shadow-xl">
                Subscribe
              </button>
            </div>
          </div>

          {/* Social links */}
          <div className="flex flex-col items-start lg:items-end justify-between gap-12">
            <div>
              <h4 className="text-lg font-bold mb-4">Follow Us</h4>
              <div className="flex items-center gap-6 mt-2">
                {socialLinks?.map((social) => (
                  <a
                    key={social?.label}
                    href={social?.url}
                    className="w-14 h-14 bg-primary/10 backdrop-blur-sm rounded-2xl flex items-center justify-center hover:bg-primary hover:scale-110 transition-all duration-300 border border-primary/20 shadow-xl"
                    aria-label={social?.label}
                  >
                    <Icon name={social?.icon} size={28} />
                  </a>
                ))}
              </div>
            </div>
            
            {/* App download buttons */}
            <div className="mt-8">
              <p className="text-sm text-white/70 mb-3">Get the mobile app</p>
              <div className="flex flex-col sm:flex-row gap-6 mt-4">
                <button className="px-10 py-4 bg-white text-primary rounded-2xl font-bold transition-all duration-300 hover:bg-primary hover:text-white hover:scale-105 text-lg shadow-xl flex items-center gap-4">
                  <Icon name="Apple" size={32} />
                  <div className="text-left">
                    <div className="text-xs text-primary">Download on</div>
                    <div className="text-lg font-bold">App Store</div>
                  </div>
                </button>
                <button className="px-10 py-4 bg-white text-primary rounded-2xl font-bold transition-all duration-300 hover:bg-primary hover:text-white hover:scale-105 text-lg shadow-xl flex items-center gap-4">
                  <Icon name="Play" size={32} />
                  <div className="text-left">
                    <div className="text-xs text-primary">Get it on</div>
                    <div className="text-lg font-bold">Google Play</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Links grid */}
  <div className="grid grid-cols-2 md:grid-cols-4 gap-16 mb-24">
          <div>
            <h4 className="font-bold text-lg mb-4">Product</h4>
            <ul className="space-y-3">
              {footerLinks?.product?.map((link) => (
                <li key={link?.label}>
                  <button
                    onClick={() => {
                      if (link?.path?.startsWith('#')) {
                        document.querySelector(link?.path)?.scrollIntoView({ behavior: 'smooth' });
                      } else {
                        navigate(link?.path);
                      }
                    }}
                    className="text-white/70 hover:text-primary transition-colors duration-300 text-left"
                  >
                    {link?.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-lg mb-4">Company</h4>
            <ul className="space-y-3">
              {footerLinks?.company?.map((link) => (
                <li key={link?.label}>
                  <button
                    onClick={() => navigate(link?.path)}
                    className="text-white/70 hover:text-primary transition-colors duration-300 text-left"
                  >
                    {link?.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-lg mb-4">Resources</h4>
            <ul className="space-y-3">
              {footerLinks?.resources?.map((link) => (
                <li key={link?.label}>
                  <button
                    onClick={() => navigate(link?.path)}
                    className="text-white/70 hover:text-primary transition-colors duration-300 text-left"
                  >
                    {link?.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-lg mb-4">Legal</h4>
            <ul className="space-y-3">
              {footerLinks?.legal?.map((link) => (
                <li key={link?.label}>
                  <button
                    onClick={() => navigate(link?.path)}
                    className="text-white/70 hover:text-primary transition-colors duration-300 text-left"
                  >
                    {link?.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Solutions Section for SEO */}
        <div className="mb-24 pb-12 border-b border-white/10">
          <h4 className="font-bold text-lg mb-6 text-center">Explore Our Solutions</h4>
          <div className="flex flex-wrap justify-center gap-4">
            {footerLinks?.solutions?.map((link) => (
              <button
                key={link?.label}
                onClick={() => navigate(link?.path)}
                className="px-4 py-2 bg-white/5 hover:bg-primary/20 rounded-lg text-white/70 hover:text-white transition-all duration-300 text-sm"
              >
                {link?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Bottom section */}
        <div className="pt-14 border-t border-primary/10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-10">
            <p className="text-white/60 text-lg">
              © {new Date()?.getFullYear()} NOONX Platform. All rights reserved.
            </p>
            <div className="flex items-center gap-12">
              <button onClick={() => navigate('/privacy-policy')} className="text-white/60 hover:text-primary text-lg font-bold transition-colors duration-300">
                Privacy Policy
              </button>
              <button onClick={() => navigate('/terms-of-service')} className="text-white/60 hover:text-primary text-lg font-bold transition-colors duration-300">
                Terms of Service
              </button>
              <button onClick={() => navigate('/cookie-policy')} className="text-white/60 hover:text-primary text-lg font-bold transition-colors duration-300">
                Cookie Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;